import type { ReactNode } from 'react'
import { useSession } from '../lib/session'
import { Wordmark } from '../ui/Wordmark'

/**
 * Holds the app back until the session has been restored (KAN-104).
 *
 * On boot the SPA does not yet know whether the visitor is signed in — the
 * answer takes one request. Rendering the routes before it arrives shows the
 * sign-in screen to someone who is already signed in, then replaces it a
 * moment later, which reads as a session that broke and recovered.
 *
 * It wraps the router rather than sitting inside `RequireAuth` so that `/login`
 * is covered too: that is the page the flash would otherwise be.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { status } = useSession()

  if (status === 'restoring') {
    return (
      <div
        className="flex min-h-full items-center justify-center px-6"
        role="status"
        aria-live="polite"
      >
        <div className="flex flex-col items-center gap-4">
          <Wordmark tone="dark" />
          {/**
           * Deliberately unlabelled. The wait is one request long, and a
           * "Restoring your session…" line is on screen too briefly to read
           * while being long enough to imply something went wrong.
           */}
          <span className="sr-only">Loading</span>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
